/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: configurations.ts
 * Class: configurations.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import _Vue from 'vue';
import { inject, provide } from '@vue/composition-api';
import { ConfigurationAPI, LocaleRouteSegments } from './types/configuration';

/**
 * simple configuration cache from the backend stored in session- or the localestorage
 */
export class ConfigurationPluginManager {
  private static instance: ConfigurationPluginManager;

  configElement: Element = document.querySelector('#vue-config-plugin-data');

  data: ConfigurationAPI;

  private constructor() {
    this.consumeDataFromDOM();
  }

  private static localeRoutePrefixReducer(previousValue: string, currentValue: string): string {
    return `${previousValue}|${currentValue}`;
  }

  public static getTerritoryRoutePattern(localeRouteSegments: LocaleRouteSegments): string {
    return localeRouteSegments
      .territories
      .reduce(ConfigurationPluginManager.localeRoutePrefixReducer);
  }

  public static getLanguageRoutePattern(localeRouteSegments: LocaleRouteSegments): string {
    return localeRouteSegments
      .languages
      .reduce(ConfigurationPluginManager.localeRoutePrefixReducer);
  }

  /**
   * returns the language of the locale prefix
   */
  public getRouteTerritory(): string {
    const segments = this.data.routes.localePrefix.split('/');

    return segments && segments.length > 1 ? segments[1] : undefined;
  }

  /**
   * returns the language of the locale prefix
   */
  public getRouteLanguage(): string {
    const segments = this.data.routes.localePrefix.split('/');

    return segments && segments.length ? segments[0] : undefined;
  }

  /**
   * returns a list of languages and countries collected
   * exploded by the localePrefixPattern
   */
  public getLocaleRouteSegments(): LocaleRouteSegments {
    const languages = new Array<string>();
    const territories = new Array<string>();

    if (!this.data.routes.patterns.localePrefixes) {
      throw new TypeError('Error: locale prefixes are missing!');
    }

    this.data.routes.patterns.localePrefixes
      .split('|')
      .map((singlePrefix) => singlePrefix.split('/'))
      .forEach((locale) => {
        locale.forEach((segment, index) => {
          if (index === 0 && !languages.includes(segment)) {
            languages.push(segment);
          } else if (!territories.includes(segment)) {
            territories.push(segment);
          }
        });
      });

    return {
      languages,
      territories,
    };
  }

  public static getInstance(): ConfigurationPluginManager {
    if (!ConfigurationPluginManager.instance) {
      ConfigurationPluginManager.instance = new ConfigurationPluginManager();
    }

    return ConfigurationPluginManager.instance;
  }

  getLocale(): string {
    return this.data.locale.replace('_', '-');
  }

  /**
   * returns the localized wiki url
   */
  getLocalizedWikiUrl(): string {
    const wikiUrl : string = process.env.VUE_APP_MENU_WIKI_URL;
    const availableWikiLanguages = ['de', 'en', 'fr', 'sp'];
    const fallbackLanguage = 'en';
    const { customer: { language } } = this.data;

    return language && availableWikiLanguages.includes(language)
      ? `${wikiUrl}/${language}`
      : `${wikiUrl}/${fallbackLanguage}`;
  }

  private consumeDataFromDOM(): void {
    if (this.configElement != undefined) {
      try {
        this.data = JSON.parse(this.configElement.textContent);
      } catch (e) {
        throw new Error('Could not decode data from DOM, backend informations may not be available!');
      }
    }
  }
}

const configurationPluginManager = ConfigurationPluginManager.getInstance();

export function VueConfigurationPlugin(Vue: typeof _Vue, options?): void {
  Vue.prototype.$api = configurationPluginManager;
}

const configSymbol = Symbol('configuration plugin');

export function provideConfigurationApi() {
  provide(configSymbol, configurationPluginManager);
}

export const useConfigurationApi = () => {
  const config = inject<ConfigurationPluginManager>(configSymbol);
  if (!config) throw new Error('Configuration Plugin could not be provided!');

  return config;
};
